import { useRef, useEffect } from "react";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Markdown } from "@/components/ui/markdown";
import { cn } from "@/lib/utils";
import { useQueryOrgData } from "./api";

const QUICK_QUESTIONS = [
  "How many contacts do I have?",
  "Show me my hot leads",
  "How are my campaigns performing?",
  "What are my open deals?",
];

export interface Message {
  id: string;
  role: "user" | "assistant";
  text: string;
  data?: unknown[];
}

interface AIInsightsSidebarProps {
  open: boolean;
  onClose: () => void;
  messages: Message[];
  onMessages: (msgs: Message[]) => void;
  question: string;
  onQuestion: (q: string) => void;
}

export function AIInsightsSidebar({
  open,
  onClose,
  messages,
  onMessages,
  question,
  onQuestion,
}: AIInsightsSidebarProps) {
  const queryData = useQueryOrgData();
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function handleAsk(q: string) {
    const text = q.trim();
    if (!text || queryData.isPending) return;
    onQuestion("");
    const userMsg: Message = { id: Date.now().toString(), role: "user", text };
    onMessages([...messages, userMsg]);
    try {
      const result = await queryData.mutateAsync(text);
      const assistantMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        text: result.answer,
        data: result.data,
      };
      onMessages([...messages, userMsg, assistantMsg]);
    } catch {
      const errorMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        text: "Sorry, I couldn't fetch that data. Try again.",
      };
      onMessages([...messages, userMsg, errorMsg]);
    }
  }

  return (
    <>
      {/* Sidebar panel */}
      <div
        className={cn(
          "fixed right-0 top-0 h-full w-80 bg-background border-l shadow-xl z-40 flex flex-col transition-transform duration-300",
          open ? "translate-x-0" : "translate-x-full",
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="font-semibold text-sm">AI Insights</span>
          </div>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Ask about your data:</p>
              {QUICK_QUESTIONS.map((q) => (
                <button
                  key={q}
                  onClick={() => void handleAsk(q)}
                  className="block w-full text-left text-xs rounded-lg border px-3 py-2 hover:bg-accent transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          )}
          {messages.map((m) => (
            <div
              key={m.id}
              className={cn(
                "rounded-lg px-3 py-2 text-sm max-w-[90%]",
                m.role === "user"
                  ? "ml-auto bg-primary text-primary-foreground"
                  : "bg-muted text-foreground",
              )}
            >
              {m.role === "assistant" ? <Markdown>{m.text}</Markdown> : m.text}
            </div>
          ))}
          {queryData.isPending && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Thinking…
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="border-t p-3 flex gap-2">
          <Input
            placeholder="Ask anything…"
            value={question}
            onChange={(e) => onQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void handleAsk(question);
            }}
            className="h-8 text-sm"
          />
          <Button
            size="icon"
            className="h-8 w-8 shrink-0"
            disabled={!question.trim() || queryData.isPending}
            onClick={() => void handleAsk(question)}
          >
            <Send className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/20"
          onClick={onClose}
          aria-hidden
        />
      )}
    </>
  );
}

/** Toggle button for placement in AppShell sidebar footer */
export function AIInsightsToggle({
  onClick,
  collapsed,
}: {
  onClick: () => void;
  collapsed: boolean;
}) {
  if (collapsed) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center justify-center rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground"
        aria-label="AI Insights"
      >
        <Sparkles className="h-5 w-5 shrink-0" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-accent/60 hover:text-foreground transition-all"
    >
      <Sparkles className="h-4 w-4 shrink-0" />
      <span>AI Insights</span>
    </button>
  );
}
